import { AssetId, AssetIdError } from "./asset-id";
import { CameraId, CameraIdError } from "./camera-id";

export class CameraKeyError extends Error {
  constructor(
    public readonly kind: "Format",
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

/**
 * A composite key uniquely identifying a Camera within an Asset.
 *
 * Implements structural equality: two CameraKeys are equal if they have
 * the same asset and camera values.
 *
 * @deprecated Use AssetCameraKey from './asset-camera-key' instead.
 */
export class CameraKey {
  constructor(
    readonly asset: AssetId,
    readonly id: CameraId,
  ) {}

  /**
   * Compares this CameraKey with another for structural equality.
   *
   * @param other - The other CameraKey to compare with
   * @returns true if both asset and camera are equal, false otherwise
   */
  equals(other: CameraKey): boolean {
    return this.asset === other.asset && this.id === other.id;
  }

  /**
   * Returns a hash code for this CameraKey.
   *
   * Equal CameraKeys will have the same hash code, making this suitable
   * for use as a Map key or in a Set.
   *
   * @returns A string hash combining the asset and camera representations
   */
  hashCode(): string {
    return this.toString();
  }

  /**
   * Returns a string representation of this CameraKey.
   *
   * @returns A string in the format "{asset}@{camera}"
   */
  toString(): string {
    return `${AssetId.toString(this.asset)}@${CameraId.toString(this.id)}`;
  }

  /**
   * Constructs a CameraKey from a string.
   *
   * @param str - A string in the format "CameraKey(asset={asset}, id={camera})"
   * @returns A CameraKey
   */
  static fromString(str: string): CameraKey {
    const parts = str.split("@");
    if (parts.length !== 2)
      throw new CameraKeyError(
        "Format",
        `Camera key ${str} was in an incorrect format. Expected an Asset ID and Camera ID joined by '@'.`,
      );
    const [assetString, cameraString] = parts as [string, string];
    try {
      const asset = AssetId.fromString(assetString);
      const camera = CameraId.fromString(cameraString);
      return new CameraKey(asset, camera);
    } catch (error) {
      if (error instanceof AssetIdError) {
        throw new CameraKeyError("Format", `Camera key ${str} has an invalid Asset ID.`, { cause: error });
      }
      if (error instanceof CameraIdError) {
        throw new CameraKeyError("Format", `Camera key ${str} has an invalid Camera ID.`, { cause: error });
      }
      throw error;
    }
  }
}
